import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { theme } from '../../theme';
import {
  BaseButton,
  BaseButtonWrapper,
  ButtonLoadingSpinner,
  ButtonProps,
  IconWrapper,
} from './BaseButton';

export type StyledPrimaryButtonProps = {
  contextColor: 'red' | 'white' | 'success';
  isActive?: boolean;
};

const successContextStyles = css`
  background-color: ${theme.colors.system.systemGreen};
  border: 1px solid ${theme.colors.system.systemGreen};
  &:active {
    background-color: ${theme.colors.system.systemGreen};
    filter: brightness(80%);
  }
`;

const redContextStyles = css`
  background-color: ${theme.colors.primary.graysBlue};
  border: 1px solid ${theme.colors.primary.graysBlue};

  &:hover {
    background-color: ${theme.colors.tints.graysBlue['_1']};
  }

  &:active {
    background-color: ${theme.colors.tints.graysBlue['_2']};
  }
`;

const whiteContextStyles = css`
  background-color: ${theme.colors.primary.graysRed};
  border: 1px solid ${theme.colors.primary.graysRed};

  &:hover {
    background-color: ${theme.colors.tints.graysRed['_1']};
  }

  &:active {
    background-color: ${theme.colors.tints.graysRed['_2']};
  }
`;

const primaryBackgroundContext = {
  red: redContextStyles,
  white: whiteContextStyles,
  success: successContextStyles,
};

const ActiveStyles = css`
  color: ${theme.colors.primary.white};
  background-color: ${theme.colors.tints.graysRed['_2']};
  fill: ${theme.colors.primary.white};
`;

export const PrimaryStyles = css<StyledPrimaryButtonProps>`
  color: ${theme.colors.primary.white};
  fill: ${theme.colors.primary.white};
  ${({ isActive }) => isActive && ActiveStyles};
  &:active {
    ${ActiveStyles};
  }
  ${({ contextColor }) => primaryBackgroundContext[contextColor]};
`;

const StyledPrimaryButton = styled(BaseButton)<StyledPrimaryButtonProps>`
  ${PrimaryStyles};
`;

export type PrimaryButtonProps = ButtonProps &
  Partial<StyledPrimaryButtonProps>;

export const PrimaryButton: React.FC<PrimaryButtonProps> = ({
  disabled = false,
  contextColor = 'white',
  isActive = false,
  fullWidth = false,
  isLoading = false,
  size = 'medium',
  iconLeft: IconLeft,
  iconRight: IconRight,
  iconFill = '',
  iconWidth = 16,
  containerId,
  children,
  ...props
}) => {
  const [isFocussed, setIsFocussed] = useState(false);

  useEffect(() => {
    if (disabled) setIsFocussed(false);
  }, [disabled]);

  return (
    <BaseButtonWrapper
      id={containerId}
      size={size}
      fullWidth={fullWidth}
      isFocussed={isFocussed}
    >
      <StyledPrimaryButton
        disabled={disabled}
        isActive={isActive}
        contextColor={contextColor}
        fullWidth={fullWidth}
        isLoading={isLoading}
        onTouchStart={() => setIsFocussed(true)}
        onFocus={() => setIsFocussed(true)}
        onMouseLeave={() => setIsFocussed(false)}
        size={size}
        {...props}
      >
        <IconWrapper>
          {IconLeft && <IconLeft fill={iconFill} width={iconWidth} />}
          {isLoading ? <ButtonLoadingSpinner size={size} /> : children}
          {IconRight && <IconRight fill={iconFill} width={iconWidth} />}
        </IconWrapper>
      </StyledPrimaryButton>
    </BaseButtonWrapper>
  );
};
